/**
 *  @class
 *  @function PairsWithCard
 *
 *  Powers the "Pairs well with" product cards: a color swatch picker (no page
 *  navigation), a dropdown for every other option (size, style/patch add-on,
 *  etc.), and a direct add-to-cart button. Mirrors the cart AJAX pattern
 *  already used by the theme's own <product-card> element so cart drawer /
 *  bubble behavior stays consistent site-wide.
 */
if (!customElements.get('pairs-with-card')) {
  class PairsWithCard extends HTMLElement {
    constructor() {
      super();
      const variantsEl = this.querySelector('.pairs-with-card--variants');
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
      this.image = this.querySelector('.pairs-with-card--img');
      // One dropdown per non-color option (size, style/patch add-on, etc.) —
      // each carries its own option index so every dimension of the variant
      // stays tracked. Untracked options previously let Add to cart silently
      // match the first variant for that dimension instead of the one shown.
      this.optionSelects = this.querySelectorAll('.pairs-with-card--size-select');
      this.swatches = this.querySelectorAll('.pairs-with-card--swatch');
      this.addButton = this.querySelector('.pairs-with-card--add-to-cart');
      this.addLabel = this.querySelector('.pairs-with-card--add-label');

      this.colorIndex = this.dataset.colorIndex !== undefined ? parseInt(this.dataset.colorIndex, 10) : null;

      try {
        this.currentOptions = JSON.parse(this.dataset.defaultOptions || '[]');
      } catch (e) {
        this.currentOptions = [];
      }
    }

    connectedCallback() {
      this.optionSelects.forEach((select) => {
        const optionIndex = select.dataset.optionIndex !== undefined ? parseInt(select.dataset.optionIndex, 10) : null;
        select.addEventListener('change', () => {
          if (optionIndex !== null) {
            this.currentOptions[optionIndex] = select.value;
          }
          this.updateState();
        });
      });

      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', (evt) => {
          evt.preventDefault();
          if (this.colorIndex !== null) {
            this.currentOptions[this.colorIndex] = swatch.dataset.value;
          }
          this.swatches.forEach((s) => {
            s.classList.remove('active');
            s.setAttribute('aria-pressed', 'false');
          });
          swatch.classList.add('active');
          swatch.setAttribute('aria-pressed', 'true');

          if (this.image && swatch.dataset.image) {
            this.image.removeAttribute('srcset');
            this.image.removeAttribute('data-srcset');
            this.image.src = swatch.dataset.image;
          }

          this.updateState();
        });
      });

      if (this.addButton) {
        this.addButton.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.addToCart();
        });
      }

      this.updateState();
    }

    findVariant() {
      return this.variants.find((variant) => {
        return variant.options.every((value, index) => {
          if (this.currentOptions[index] === undefined || this.currentOptions[index] === null) {
            return true;
          }
          return value === this.currentOptions[index];
        });
      });
    }

    updateState() {
      this.currentVariant = this.findVariant();

      if (!this.addButton) {
        return;
      }

      // Note: the label intentionally always reads "Add to cart" (set in the
      // Liquid template) regardless of variant availability. We still toggle
      // disabled state below so out-of-stock variants can't be added, but we
      // no longer overwrite the button's text here.
      if (this.currentVariant && this.currentVariant.available) {
        this.addButton.disabled = false;
        this.addButton.removeAttribute('aria-disabled');
      } else {
        this.addButton.disabled = true;
        this.addButton.setAttribute('aria-disabled', true);
      }
    }

    addToCart() {
      if (!this.currentVariant || this.addButton.disabled || this.addButton.classList.contains('loading')) {
        return;
      }

      this.addButton.classList.add('loading');
      this.addButton.setAttribute('aria-disabled', true);

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/javascript',
        },
      };

      const formData = new FormData();
      formData.append('id', this.currentVariant.id);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }
          this.renderContents(response);
          document.dispatchEvent(
            new CustomEvent('cart:item-added', {
              detail: {
                product: response.hasOwnProperty('items') ? response.items[0] : response,
              },
            })
          );
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.addButton.classList.remove('loading');
          this.addButton.removeAttribute('aria-disabled');
        });
    }

    getSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' },
      ];
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        const container = document.getElementById(section.id);
        if (!container) {
          return;
        }
        const elementToReplace = container.querySelector(section.selector) || container;
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (section.id === 'Cart-Drawer') {
          const drawer = document.getElementById('Cart-Drawer');
          if (drawer && typeof drawer.notesToggle === 'function') {
            drawer.notesToggle();
          }
          if (drawer && typeof drawer.removeProductEvent === 'function') {
            drawer.removeProductEvent();
          }
        }

        if (section.id === 'Cart' && typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      });

      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        cartDrawer.classList.add('active');
        document.body.classList.add('open-cart');
        document.body.classList.add('open-cc');
        const fullRecs = cartDrawer.querySelector('.product-recommendations--full');
        if (fullRecs) {
          fullRecs.classList.add('active');
        }
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
      }
    }
  }

  customElements.define('pairs-with-card', PairsWithCard);
}

/**
 *  @class
 *  @function ComplementaryProductsScroll
 *
 *  Wraps the "Pairs well with" product grid when it holds more than 2
 *  products. Powers the arrow button that scrolls the row sideways one
 *  "page" at a time, and hides the arrow once the row has been scrolled
 *  to its end (native drag/swipe still works either way).
 */
if (!customElements.get('complementary-products-scroll')) {
  class ComplementaryProductsScroll extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.complementary-products--grid');
      this.nextArrow = this.querySelector('.complementary-products--scroll-arrow--next');
      this.prevArrow = this.querySelector('.complementary-products--scroll-arrow--prev');
    }

    connectedCallback() {
      if (!this.track) {
        return;
      }

      if (this.nextArrow) {
        this.onNextClick = () => {
          this.track.scrollBy({ left: this.track.clientWidth * 0.9, behavior: 'smooth' });
        };
        this.nextArrow.addEventListener('click', this.onNextClick);
      }

      if (this.prevArrow) {
        this.onPrevClick = () => {
          this.track.scrollBy({ left: -this.track.clientWidth * 0.9, behavior: 'smooth' });
        };
        this.prevArrow.addEventListener('click', this.onPrevClick);
      }

      this.onScroll = () => this.updateArrows();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);

      this.updateArrows();
    }

    disconnectedCallback() {
      if (this.nextArrow) {
        this.nextArrow.removeEventListener('click', this.onNextClick);
      }
      if (this.prevArrow) {
        this.prevArrow.removeEventListener('click', this.onPrevClick);
      }
      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
      }
      window.removeEventListener('resize', this.onScroll);
    }

    updateArrows() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const atStart = this.track.scrollLeft <= 4;
      const atEnd = maxScroll <= 1 || this.track.scrollLeft >= maxScroll - 4;

      if (this.nextArrow) {
        this.nextArrow.classList.toggle('is-hidden', atEnd);
      }
      if (this.prevArrow) {
        this.prevArrow.classList.toggle('is-hidden', atStart);
      }
    }
  }

  customElements.define('complementary-products-scroll', ComplementaryProductsScroll);
}
